import React, { useEffect, useState, useCallback } from 'react';
import './dashboard.css';
import { showWarning } from '../utils/sweetAlertHelper';
import {
  FaUsers,
  FaPhoneAlt,
  FaFileSignature,
  FaHourglassHalf,
  FaTimesCircle,
  FaCalendarAlt,
  FaFilter,
  FaSearch,
  FaSyncAlt
} from 'react-icons/fa';
import { Bar, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  ArcElement,
  Tooltip,
  Legend
} from 'chart.js';
import axios from 'axios';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import weekOfYear from 'dayjs/plugin/weekOfYear';

// Register plugins
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(weekOfYear);

// Register Chart.js components
ChartJS.register(
  BarElement,
  CategoryScale,
  LinearScale,
  ArcElement,
  Tooltip,
  Legend
);

const Dashboard = () => {
  const [allLeads, setAllLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [user, setUser] = useState(null);
  const [userStats, setUserStats] = useState([]);
  const [dateFilter, setDateFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const updateUserStats = useCallback((leads, userData) => {
    if (userData?.role === 'admin') {
      const stats = {};
      leads.forEach((lead) => {
        const assignedUser = lead.assignedTo;
        if (assignedUser && assignedUser._id) {
          const userId = assignedUser._id;
          if (!stats[userId]) {
            stats[userId] = {
              name: assignedUser.name || 'Unnamed',
              assignedCount: 0,
              convertedCount: 0,
              contactedCount: 0,
              disqualifiedCount: 0,
              proposalSharedCount: 0,
              inProcessCount: 0,
              demoScheduledCount: 0,
              pendingCount: 0,
            };
          }
          stats[userId].assignedCount += 1;

          if (lead.status === 'qualified') stats[userId].convertedCount += 1;
          if (lead.status === 'contacted') stats[userId].contactedCount += 1;
          if (lead.status === 'disqualified') stats[userId].disqualifiedCount += 1;
          if (lead.status === 'proposal-shared') stats[userId].proposalSharedCount += 1;
          if (lead.status === 'inprocess') stats[userId].inProcessCount += 1;
          if (lead.status === 'demo-scheduled') stats[userId].demoScheduledCount += 1;
          if (lead.status === 'new') stats[userId].pendingCount += 1;
        }
      });
      setUserStats(Object.values(stats));
    } else if (userData?.role === 'vendor') {
      // Vendor: show team members' performance (users assigned to leads visible to this vendor)
      const stats = {};
      leads.forEach((lead) => {
        const assignedUser = lead.assignedTo;
        if (assignedUser && assignedUser._id) {
          const userId = assignedUser._id;
          if (!stats[userId]) {
            stats[userId] = {
              name: assignedUser.name || 'Unnamed',
              assignedCount: 0,
              convertedCount: 0,
              contactedCount: 0,
              disqualifiedCount: 0,
              proposalSharedCount: 0,
              inProcessCount: 0,
              demoScheduledCount: 0,
              pendingCount: 0,
            };
          }
          stats[userId].assignedCount += 1;

          if (lead.status === 'qualified') stats[userId].convertedCount += 1;
          if (lead.status === 'contacted') stats[userId].contactedCount += 1;
          if (lead.status === 'disqualified') stats[userId].disqualifiedCount += 1;
          if (lead.status === 'proposal-shared') stats[userId].proposalSharedCount += 1;
          if (lead.status === 'inprocess') stats[userId].inProcessCount += 1;
          if (lead.status === 'demo-scheduled') stats[userId].demoScheduledCount += 1;
          if (lead.status === 'new') stats[userId].pendingCount += 1;
        }
      });
      setUserStats(Object.values(stats));
    }
  }, []);

  const filterLeads = useCallback((leads, filter, userData) => {
    let filtered = leads;
    
    switch(filter) {
      case 'today':
        filtered = filtered.filter(lead => 
          dayjs(lead.createdAt).isSame(dayjs(), 'day')
        );
        break;
      case 'week':
        filtered = filtered.filter(lead => 
          dayjs(lead.createdAt).week() === dayjs().week() &&
          dayjs(lead.createdAt).year() === dayjs().year()
        );
        break;
      case 'month':
        filtered = filtered.filter(lead => 
          dayjs(lead.createdAt).month() === dayjs().month() && 
          dayjs(lead.createdAt).year() === dayjs().year()
        );
        break;
      case 'custom':
        if (startDate && endDate) {
          filtered = filtered.filter(lead => 
            dayjs(lead.createdAt).isSameOrAfter(dayjs(startDate), 'day') && 
            dayjs(lead.createdAt).isSameOrBefore(dayjs(endDate), 'day')
          );
        }
        break;
      default:
        break;
    }

    if (userData?.role !== 'admin') {
      // Vendor: show leads assigned to them OR created by them
      filtered = filtered.filter(lead => 
        lead.assignedTo?._id === userData._id || lead.createdBy?._id === userData._id
      );
    }

    setFilteredLeads(filtered);
    updateUserStats(filtered, userData);
  }, [startDate, endDate, updateUserStats]);

  useEffect(() => {
    const fetchLeads = async () => {
      try {
        const token = localStorage.getItem('token');
        const userData = JSON.parse(localStorage.getItem('user'));
        setUser(userData);

        const res = await axios.get(`${process.env.REACT_APP_API_URL}/leads/list`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        setAllLeads(res.data);
        filterLeads(res.data, dateFilter, userData);
      } catch (error) {
        console.error('Error fetching leads:', error);
      } 
    };

    fetchLeads();
  }, [filterLeads, dateFilter]);

  useEffect(() => {
    if (allLeads.length > 0 && user) {
      filterLeads(allLeads, dateFilter, user);
    }
  }, [dateFilter, allLeads, user, startDate, endDate, filterLeads]);

  const handleCustomDateSearch = () => {
    if (startDate && endDate) {
      setDateFilter('custom');
    } else {
      showWarning('Please select both start and end dates');
    }
  };

  const handleResetDates = () => {
    setStartDate('');
    setEndDate('');
    setDateFilter('all');
  };

  const countByStatus = (status) =>
    filteredLeads.filter((lead) => lead.status === status).length;

  const totalLeads = filteredLeads.length;
  const contactedLeads = countByStatus('contacted');
  const proposalsConverted = countByStatus('qualified');
  const pendingLeads = countByStatus('new');
  const notQualified = countByStatus('disqualified');
  const inProcess = countByStatus('inprocess');
  const demoScheduled = countByStatus('demo-scheduled');
  const proposalShared = countByStatus('proposal-shared');

  const itLeads = filteredLeads.filter((lead) => lead.leadsType?.toLowerCase() === 'it').length;
  const b2bLeads = filteredLeads.filter((lead) => lead.leadsType?.toLowerCase() === 'b2b').length;
  const hotLeads = filteredLeads.filter((lead) => lead.leadTemperature?.toLowerCase() === 'hot').length;

  const todayLeads = filteredLeads.filter((lead) =>
    dayjs(lead.createdAt).isSame(dayjs(), 'day')
  ).length;

  const weeklyLeads = filteredLeads.filter((lead) =>
    dayjs(lead.createdAt).week() === dayjs().week()
  ).length;

  const monthlyLeads = filteredLeads.filter((lead) =>
    dayjs(lead.createdAt).month() === dayjs().month() && 
    dayjs(lead.createdAt).year() === dayjs().year()
  ).length;

  const cards = [
    { title: "Total Leads", count: totalLeads, icon: <FaUsers />, bgClass: "card-blue" },
    { title: "Contacted Leads", count: contactedLeads, icon: <FaPhoneAlt />, bgClass: "card-green" },
    { title: "Converted Leads", count: proposalsConverted, icon: <FaFileSignature />, bgClass: "card-purple" },
    { title: "Pending Leads", count: pendingLeads, icon: <FaHourglassHalf />, bgClass: "card-yellow" },
    { title: "Not Qualified", count: notQualified, icon: <FaTimesCircle />, bgClass: "card-red" },
    { title: "In Process", count: inProcess, icon: <FaHourglassHalf />, bgClass: "card-orange" },
    { title: "Demo Scheduled", count: demoScheduled, icon: <FaCalendarAlt />, bgClass: "card-pink" },
    { title: "Proposal Shared", count: proposalShared, icon: <FaFileSignature />, bgClass: "card-teal" },
    { title: "IT Leads", count: itLeads, icon: <FaUsers />, bgClass: "card-dark" },
    { title: "B2B Leads", count: b2bLeads, icon: <FaUsers />, bgClass: "card-lightblue" },
    { title: "Hot Leads", count: hotLeads, icon: <FaUsers />, bgClass: "card-hot" },
    { title: "Today's Leads", count: todayLeads, icon: <FaUsers />, bgClass: "card-today" },
    { title: "Weekly Leads", count: weeklyLeads, icon: <FaUsers />, bgClass: "card-week" },
    { title: "Monthly Leads", count: monthlyLeads, icon: <FaUsers />, bgClass: "card-month" },
  ];

  const leadVsContactChart = {
    labels: ['Total Leads', 'Contacted', 'Converted', 'Pending'],
    datasets: [
      {
        label: 'Leads',
        data: [totalLeads, contactedLeads, proposalsConverted, pendingLeads],
        backgroundColor: [
          '#3498db',
          '#2ecc71',
          '#9b59b6',
          '#f1c40f'
        ],
        borderColor: [
          '#2980b9',
          '#27ae60',
          '#8e44ad',
          '#f39c12'
        ],
        borderWidth: 1,
      },
    ],
  };

  const statusDistributionChart = {
    labels: ['Contacted', 'Converted', 'Pending', 'Not Qualified', 'In Process', 'Demo Scheduled', 'Proposal Shared'],
    datasets: [
      {
        data: [contactedLeads, proposalsConverted, pendingLeads, notQualified, inProcess, demoScheduled, proposalShared],
        backgroundColor: [
          '#2ecc71',
          '#9b59b6',
          '#f1c40f',
          '#e74c3c',
          '#e67e22',
          '#3498db',
          '#1abc9c'
        ],
        borderColor: [
          '#27ae60',
          '#8e44ad',
          '#f39c12',
          '#c0392b',
          '#d35400',
          '#2980b9',
          '#16a085'
        ],
        borderWidth: 1,
      },
    ],
  };

  const getFilterDateRange = () => {
    switch(dateFilter) {
      case 'today':
        return dayjs().format('MMMM D, YYYY');
      case 'week':
        return `${dayjs().startOf('week').format('MMM D')} - ${dayjs().endOf('week').format('MMM D, YYYY')}`;
      case 'month':
        return dayjs().format('MMMM YYYY');
      case 'custom':
        return `${dayjs(startDate).format('MMM D, YYYY')} - ${dayjs(endDate).format('MMM D, YYYY')}`;
      default:
        return 'All Time';
    }
  };

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>Dashboard Overview</h1>
        <div className="date-range-display">
          <FaCalendarAlt /> {getFilterDateRange()}
        </div>
      </div>

      <div className="date-filter-container">
        <div className="date-filter">
          <button 
            className={dateFilter === 'all' ? 'active' : ''} 
            onClick={() => setDateFilter('all')}
          >
            All Time
          </button>
          <button 
            className={dateFilter === 'today' ? 'active' : ''} 
            onClick={() => setDateFilter('today')}
          >
            Today
          </button>
          <button 
            className={dateFilter === 'week' ? 'active' : ''} 
            onClick={() => setDateFilter('week')}
          >
            This Week
          </button>
          <button 
            className={dateFilter === 'month' ? 'active' : ''} 
            onClick={() => setDateFilter('month')}
          >
            This Month
          </button>
          <div className="custom-date-filter">
            <FaFilter />
            <input 
              type="date" 
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="date-input"
              placeholder="Start Date"
            />
            <span>to</span>
            <input 
              type="date" 
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="date-input"
              placeholder="End Date"
            />
            <button 
              className="search-date-btn"
              onClick={handleCustomDateSearch}
            >
              <FaSearch /> Search
            </button>
            <button 
              className="reset-date-btn"
              onClick={handleResetDates}
            >
              <FaSyncAlt /> Reset
            </button>
          </div>
        </div>
      </div>

      <div className="metrics-grid">
        {cards.map((card, index) => (
          <div key={index} className={`metric-card ${card.bgClass}`}>
            <div className="metric-content">
              <div className="metric-text">
                <h3>{card.title}</h3>
                <p>{card.count}</p>
              </div>
              <div className="metric-icon">{card.icon}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="charts-section">
        <div className="chart-container">
          <div className="chart-header">
            <h3>Leads Overview</h3>
          </div>
          <div className="chart-wrapper">
            <Bar 
              data={leadVsContactChart} 
              options={{
                responsive: true,
                plugins: {
                  legend: {
                    position: 'top',
                  },
                  tooltip: {
                    mode: 'index',
                    intersect: false,
                  },
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    ticks: {
                      stepSize: 1
                    }
                  }
                }
              }}
            />
          </div>
        </div>

        <div className="chart-container">
          <div className="chart-header">
            <h3>Status Distribution</h3>
          </div>
          <div className="chart-wrapper">
            <Pie 
              data={statusDistributionChart} 
              options={{
                responsive: true,
                plugins: {
                  legend: {
                    position: 'right',
                  },
                }
              }}
            />
          </div>
        </div>
      </div>

      {(user?.role === 'admin' || user?.role === 'vendor') && (
        <div className="user-performance-section">
          <div className="section-header">
            <h2>{user?.role === 'admin' ? 'User Performance Metrics' : 'Team Performance Metrics'}</h2>
          </div>
          <div className="table-responsive">
            <table className="user-stats-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Assigned</th>
                  <th>Contacted</th>
                  <th>Converted</th>
                  <th>Disqualified</th>
                  <th>Proposal Shared</th>
                  <th>In Process</th>
                  <th>Demo Scheduled</th>
                  <th>Pending</th>
                  <th>Conversion Rate</th>
                </tr>
              </thead>
              <tbody>
                {userStats.map((u, index) => (
                  <tr key={index}>
                    <td className="user-name">{u.name}</td>
                    <td>{u.assignedCount}</td>
                    <td>{u.contactedCount}</td>
                    <td className="highlight">{u.convertedCount}</td>
                    <td>{u.disqualifiedCount}</td>
                    <td>{u.proposalSharedCount}</td>
                    <td>{u.inProcessCount}</td>
                    <td>{u.demoScheduledCount}</td>
                    <td>{u.pendingCount}</td>
                    <td className="highlight">
                      {u.assignedCount > 0 
                        ? `${((u.convertedCount / u.assignedCount) * 100).toFixed(1)}%` 
                        : '0%'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}


    </div>
  );
};

export default Dashboard;